import { ColDef } from 'ag-grid-enterprise';

interface CSVAllocationData {
  isin: string;
  current_allocation: number;
  sector: string;
  ticker?: string;
  name?: string;
}

export const createCSVTableColumnDefs = (
  data: CSVAllocationData[],
): ColDef[] => {
  if (data.length === 0) return [];

  // Check if ticker and name columns exist in the data
  const hasTicker = data.some((row) => row.ticker);
  const hasName = data.some((row) => row.name);

  // Tooltip getter for error messages
  const errorTooltip = (params: any) => {
    if (params.data?._hasError && params.data?._errorMessage) {
      return `⚠ ${params.data._errorMessage}`;
    }
    return null;
  };

  const columns: ColDef[] = [
    {
      field: 'isin',
      headerName: 'ISIN',
      sortable: false,
      filter: false,
      resizable: true,
      flex: 1,
      minWidth: 150,
      cellClass: 'flex align-items-center justify-content-left',
      wrapText: true,
      tooltipValueGetter: errorTooltip,
    },
  ];

  // Add ticker column if data contains ticker values
  if (hasTicker) {
    columns.push({
      field: 'ticker',
      headerName: 'Ticker',
      sortable: false,
      filter: false,
      resizable: true,
      flex: 1,
      minWidth: 100,
      cellClass: 'flex align-items-center justify-content-left',
      wrapText: true,
      editable: false, // Read-only for display
      tooltipValueGetter: errorTooltip,
    });
  }

  // Add name column if data contains name values
  if (hasName) {
    columns.push({
      field: 'name',
      headerName: 'Company Name',
      sortable: false,
      filter: false,
      resizable: true,
      flex: 1,
      minWidth: 200,
      cellClass: 'flex align-items-center justify-content-left',
      wrapText: true,
      editable: false, // Read-only for display
      tooltipValueGetter: errorTooltip,
    });
  }

  // Add the rest of the columns
  columns.push(
    {
      field: 'sector',
      headerName: 'Sector',
      sortable: false,
      filter: false,
      resizable: true,
      flex: 1,
      minWidth: 150,
      cellClass: 'flex align-items-center justify-content-left',
      wrapText: true,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['c', 'd'],
      },
      valueSetter: (params) => {
        if (params.newValue === 'c' || params.newValue === 'd') {
          params.data.sector = params.newValue;
          return true;
        }
        return false;
      },
      valueFormatter: (params) => {
        if (!params.value || (params.value !== 'c' && params.value !== 'd')) {
          return '';
        }
        return params.value.toUpperCase();
      },
      tooltipValueGetter: errorTooltip,
    },
    {
      field: 'current_allocation',
      headerName: 'Current Allocation (%)',
      sortable: false,
      filter: false,
      resizable: true,
      flex: 1,
      minWidth: 150,
      cellClass: 'flex align-items-center justify-content-left',
      wrapText: true,
      valueFormatter: (params) => {
        if (params.value === null || params.value === undefined) return '-';
        return `${params.value}%`;
      },
      tooltipValueGetter: errorTooltip,
    },
  );

  return columns;
};
